import React, { useState, useEffect } from 'react';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { subscriptionService } from '../../../services/subscriptionService';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDateInBarbados } from '../../../utils/timezone';
import { supabase } from '../../../lib/supabase';

const SubscriptionDashboard = () => {
  const { subscription, loading, hasProAccess, upgradeToProCheckout, openCustomerPortal } = useSubscription();
  const { user } = useAuth();
  const [actionLoading, setActionLoading] = useState(false);
  const [usageLimits, setUsageLimits] = useState({
    accounts: { current: 0, limit: 3 },
    recurring: { current: 0, limit: 1 },
    budgets: { current: 0, limit: 1 },
    alerts: { current: 0, limit: 1 }
  });
  const [loadingUsage, setLoadingUsage] = useState(true);

  useEffect(() => {
    loadUsageData();
  }, [user]);

  const loadUsageData = async () => {
    if (!user) return;

    setLoadingUsage(true);
    try {
      // Fetch current usage counts
      const [accountsRes, recurringRes, budgetsRes, alertsRes] = await Promise.all([
        supabase?.from('accounts')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id),
        supabase?.from('recurring_transactions')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id),
        supabase?.from('budget_limits')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id),
        supabase?.from('budget_alerts')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id)
      ]);

      setUsageLimits({
        accounts: {
          current: accountsRes?.count || 0,
          limit: hasProAccess ? null : subscriptionService?.FREE_LIMITS?.MAX_ACCOUNTS
        },
        recurring: {
          current: recurringRes?.count || 0,
          limit: hasProAccess ? null : subscriptionService?.FREE_LIMITS?.MAX_RECURRING
        },
        budgets: {
          current: budgetsRes?.count || 0,
          limit: hasProAccess ? null : subscriptionService?.FREE_LIMITS?.MAX_BUDGETS
        },
        alerts: {
          current: alertsRes?.count || 0,
          limit: hasProAccess ? null : subscriptionService?.FREE_LIMITS?.MAX_ALERTS
        }
      });
    } catch (error) {
      console.error('Error loading usage data:', error);
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleUpgrade = async () => {
    setActionLoading(true);
    try {
      await upgradeToProCheckout();
    } catch (err) {
      console.error('Upgrade error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    try {
      await openCustomerPortal();
    } catch (err) {
      console.error('Portal error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateInBarbados(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': case'trialing':
        return 'text-green-600 bg-green-50';
      case 'past_due':
        return 'text-yellow-600 bg-yellow-50';
      case 'canceled': case'unpaid':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getUsagePercentage = (current, limit) => {
    if (!limit) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading || loadingUsage) {
    return (
      <div className="space-y-24">
        <div className="animate-pulse space-y-16">
          <div className="h-6 bg-muted rounded w-1/4"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-48 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const isPastDue = subscription?.subscription_status === 'past_due';
  const planName = subscriptionService?.formatPlan(subscription?.plan || 'FREE');
  const statusName = subscriptionService?.formatStatus(subscription?.subscription_status || 'inactive');

  return (
    <div className="space-y-24">
      {/* Active Plan Card */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-24">
        <div className="flex items-start justify-between mb-24">
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground mb-8">
              Active Plan
            </h3>
            <p className="text-sm text-muted-foreground">
              Your current subscription details and status
            </p>
          </div>
          {hasProAccess && (
            <span className="inline-flex items-center gap-6 px-12 py-6 bg-primary/10 text-primary rounded-full text-xs font-medium">
              <Icon name="Crown" size={14} />
              Pro Member
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-24">
          <div className="bg-muted/50 rounded-lg p-16">
            <div className="flex items-center gap-8 mb-8">
              <Icon name="Package" size={16} color="var(--color-muted-foreground)" />
              <span className="text-xs text-muted-foreground font-medium">Plan</span>
            </div>
            <p className="text-lg font-semibold text-foreground">{planName}</p>
          </div>

          <div className="bg-muted/50 rounded-lg p-16">
            <div className="flex items-center gap-8 mb-8">
              <Icon name="Activity" size={16} color="var(--color-muted-foreground)" />
              <span className="text-xs text-muted-foreground font-medium">Status</span>
            </div>
            <span className={`inline-flex items-center px-8 py-4 rounded-full text-xs font-medium ${
              getStatusColor(subscription?.subscription_status || 'inactive')
            }`}>
              {statusName}
            </span>
          </div>

          {subscription?.current_period_end && hasProAccess && (
            <div className="bg-muted/50 rounded-lg p-16">
              <div className="flex items-center gap-8 mb-8">
                <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                <span className="text-xs text-muted-foreground font-medium">Next Billing</span>
              </div>
              <p className="text-sm font-semibold text-foreground">
                {formatDate(subscription?.current_period_end)}
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-12 pt-16 border-t border-border">
          {!hasProAccess ? (
            <Button
              onClick={handleUpgrade}
              disabled={actionLoading}
              iconName="ArrowUp"
              iconPosition="left"
            >
              {actionLoading ? 'Processing...' : 'Upgrade to Pro'}
            </Button>
          ) : (
            <Button
              onClick={handleManageSubscription}
              disabled={actionLoading}
              variant="outline"
              iconName="Settings"
              iconPosition="left"
            >
              {actionLoading ? 'Loading...' : 'Manage Subscription'}
            </Button>
          )}
          {isPastDue && (
            <Button
              onClick={handleManageSubscription}
              disabled={actionLoading}
              iconName="CreditCard"
              iconPosition="left"
            >
              Update Payment
            </Button>
          )}
        </div>
      </div>

      {/* Usage Limits Card */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-24">
        <div className="mb-24">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-8">
            Usage Limits
          </h3>
          <p className="text-sm text-muted-foreground">
            {hasProAccess 
              ? 'You have unlimited access to all features' :'Track your usage against Free plan limits'
            }
          </p>
        </div>

        <div className="space-y-20">
          {/* Accounts Usage */}
          <div>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-8">
                <Icon name="Wallet" size={16} color="var(--color-foreground)" />
                <span className="text-sm font-medium text-foreground">Accounts</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {usageLimits?.accounts?.current} {hasProAccess ? '' : `/ ${usageLimits?.accounts?.limit}`}
                {hasProAccess && <span className="ml-4 text-xs text-primary">(Unlimited)</span>}
              </span>
            </div>
            {!hasProAccess && (
              <div className="w-full bg-muted rounded-full h-8">
                <div
                  className={`h-8 rounded-full transition-all ${
                    getUsageColor(getUsagePercentage(usageLimits?.accounts?.current, usageLimits?.accounts?.limit))
                  }`}
                  style={{ width: `${getUsagePercentage(usageLimits?.accounts?.current, usageLimits?.accounts?.limit)}%` }}
                ></div>
              </div>
            )}
          </div>

          {/* Recurring Transactions Usage */}
          <div>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-8">
                <Icon name="Repeat" size={16} color="var(--color-foreground)" />
                <span className="text-sm font-medium text-foreground">Recurring Transactions</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {usageLimits?.recurring?.current} {hasProAccess ? '' : `/ ${usageLimits?.recurring?.limit}`}
                {hasProAccess && <span className="ml-4 text-xs text-primary">(Unlimited)</span>}
              </span>
            </div>
            {!hasProAccess && (
              <div className="w-full bg-muted rounded-full h-8">
                <div
                  className={`h-8 rounded-full transition-all ${
                    getUsageColor(getUsagePercentage(usageLimits?.recurring?.current, usageLimits?.recurring?.limit))
                  }`}
                  style={{ width: `${getUsagePercentage(usageLimits?.recurring?.current, usageLimits?.recurring?.limit)}%` }}
                ></div>
              </div>
            )}
          </div>

          {/* Budgets Usage */}
          <div>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-8">
                <Icon name="PieChart" size={16} color="var(--color-foreground)" />
                <span className="text-sm font-medium text-foreground">Budgets</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {usageLimits?.budgets?.current} {hasProAccess ? '' : `/ ${usageLimits?.budgets?.limit}`}
                {hasProAccess && <span className="ml-4 text-xs text-primary">(Unlimited)</span>}
              </span>
            </div>
            {!hasProAccess && (
              <div className="w-full bg-muted rounded-full h-8">
                <div
                  className={`h-8 rounded-full transition-all ${
                    getUsageColor(getUsagePercentage(usageLimits?.budgets?.current, usageLimits?.budgets?.limit))
                  }`}
                  style={{ width: `${getUsagePercentage(usageLimits?.budgets?.current, usageLimits?.budgets?.limit)}%` }}
                ></div>
              </div>
            )}
          </div>

          {/* Alerts Usage */}
          <div>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-8">
                <Icon name="Bell" size={16} color="var(--color-foreground)" />
                <span className="text-sm font-medium text-foreground">Budget Alerts</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {usageLimits?.alerts?.current} {hasProAccess ? '' : `/ ${usageLimits?.alerts?.limit}`}
                {hasProAccess && <span className="ml-4 text-xs text-primary">(Unlimited)</span>}
              </span>
            </div>
            {!hasProAccess && (
              <div className="w-full bg-muted rounded-full h-8">
                <div
                  className={`h-8 rounded-full transition-all ${
                    getUsageColor(getUsagePercentage(usageLimits?.alerts?.current, usageLimits?.alerts?.limit))
                  }`}
                  style={{ width: `${getUsagePercentage(usageLimits?.alerts?.current, usageLimits?.alerts?.limit)}%` }}
                ></div>
              </div>
            )}
          </div>
        </div>

        {!hasProAccess && (
          <div className="mt-20 p-16 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-start gap-12">
              <Icon name="Info" size={16} color="var(--color-primary)" />
              <div className="flex-1">
                <p className="text-sm text-foreground mb-4">
                  <strong>Upgrade to Pro</strong> for unlimited access
                </p>
                <p className="text-xs text-muted-foreground">
                  Remove all limits and unlock premium features like financial reports, transfers, and split transactions.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Billing History Card */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-24">
        <div className="mb-24">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-8">
            Billing History
          </h3>
          <p className="text-sm text-muted-foreground">
            View and manage your payment history
          </p>
        </div>

        {hasProAccess ? (
          <div>
            <div className="flex items-center justify-between p-16 bg-muted/50 rounded-lg mb-12">
              <div className="flex items-center gap-12">
                <div className="w-40 h-40 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="Receipt" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Pro Subscription</p>
                  <p className="text-xs text-muted-foreground">
                    {subscription?.current_period_end 
                      ? `Active until ${formatDate(subscription?.current_period_end)}`
                      : 'Active subscription'
                    }
                  </p>
                </div>
              </div>
              <span className="text-sm font-semibold text-foreground">Active</span>
            </div>

            <Button
              onClick={handleManageSubscription}
              disabled={actionLoading}
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
              className="w-full"
            >
              View Full Billing History in Stripe
            </Button>
          </div>
        ) : (
          <div className="text-center py-32">
            <div className="w-48 h-48 rounded-full bg-muted mx-auto mb-16 flex items-center justify-center">
              <Icon name="Receipt" size={24} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm text-muted-foreground mb-16">
              No billing history available
            </p>
            <p className="text-xs text-muted-foreground mb-20">
              Upgrade to Pro to start your subscription
            </p>
            <Button
              onClick={handleUpgrade}
              disabled={actionLoading}
              size="sm"
              iconName="ArrowUp"
              iconPosition="left"
            >
              Upgrade to Pro
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDashboard;